import { Link, createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Key,
  LockKeyholeOpen,
  Pencil,
  PlusIcon,
  Zap,
} from "lucide-react";
import type { GatewayKeyItem } from "@/lib/queries/gateway-keys";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  useGatewayKeysList,
  useRenameGatewayKey,
  useRevokeGatewayKey,
} from "@/lib/queries/gateway-keys";
import { useResourceAccessPermission } from "@/lib/perms";

export const Route = createFileRoute("/_app/gateway-keys/")({
  component: RouteComponent,
  staticData: {
    permissionGuards: [
      {
        resource: "gateway_keys",
      },
    ],
  },
});

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function getPrimaryProvider(providers: Record<string, Array<string>>) {
  return Object.keys(providers)[0] ?? "";
}

function getModels(providers: Record<string, Array<string>>) {
  return Object.values(providers).flat().join(", ");
}

type AccessLabel = "Allowed" | "Blocked" | "Redact";

function policyModeToAccess(policyMode: string): AccessLabel {
  if (policyMode === "Blocklist") return "Blocked";
  if (policyMode === "Redactlist") return "Redact";
  return "Allowed";
}

function KeyCard({ k, onClick }: { k: GatewayKeyItem; onClick?: () => void }) {
  const isRevoked = k.status === "revoked";
  const primaryProvider = getPrimaryProvider(k.providers);
  const models = getModels(k.providers);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      className={cn(
        "cursor-pointer rounded-xl border px-5 py-4 shadow-sm transition-colors",
        isRevoked
          ? "border-slate-200/70 bg-slate-50/60 hover:bg-slate-100/60 dark:border-border dark:bg-muted/40 dark:hover:bg-muted/60"
          : "bg-white hover:bg-slate-50 dark:bg-card dark:hover:bg-accent",
      )}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <div
            className={cn(
              "flex size-10 shrink-0 items-center justify-center rounded-lg",
              isRevoked
                ? "bg-muted/60 text-muted-foreground/50 ring-1 ring-muted-foreground/10"
                : "bg-[#0B1220] text-white dark:bg-foreground dark:text-background",
            )}
          >
            <Key className="size-4" />
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  "truncate text-sm font-semibold",
                  isRevoked ? "text-muted-foreground/70" : "text-foreground",
                )}
              >
                {k.name}
                {primaryProvider ? ` — ${primaryProvider}` : ""}
              </span>
              {isRevoked && (
                <span className="inline-flex shrink-0 items-center rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-400 ring-1 ring-slate-200/80 ring-inset dark:bg-muted dark:text-muted-foreground dark:ring-border">
                  Revoked
                </span>
              )}
            </div>

            <div
              className={cn(
                "mt-1 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs",
                isRevoked
                  ? "text-muted-foreground/50"
                  : "text-muted-foreground",
              )}
            >
              {primaryProvider && (
                <span className="whitespace-nowrap">{primaryProvider}</span>
              )}
              {models && <span className="min-w-0 truncate">{models}</span>}
              <span className="whitespace-nowrap">
                {k.entities.length} entities
              </span>
              <span className="whitespace-nowrap">
                {formatDate(k.created_at)}
              </span>
            </div>
          </div>
        </div>

        <ChevronRight
          className={cn(
            "size-4 shrink-0",
            isRevoked ? "text-muted-foreground/40" : "text-muted-foreground",
          )}
        />
      </div>
    </div>
  );
}

const accessColor: Record<AccessLabel, { dot: string; text: string }> = {
  Allowed: {
    dot: "bg-emerald-500",
    text: "text-emerald-600 dark:text-emerald-400",
  },
  Blocked: { dot: "bg-red-500", text: "text-red-500 dark:text-red-400" },
  Redact: { dot: "bg-amber-500", text: "text-amber-500 dark:text-amber-400" },
};

function KeyDetailDialog({
  gatewayKey,
  open,
  onOpenChange,
  onRevoked,
  onRenamed,
}: {
  gatewayKey: GatewayKeyItem | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onRevoked?: () => void;
  onRenamed?: (key: GatewayKeyItem) => void;
}) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [editValue, setEditValue] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);

  const canDelete = useResourceAccessPermission("gateway_keys", ["delete"]);
  const canUpdate = useResourceAccessPermission("gateway_keys", ["update"]);

  const revokeMutation = useRevokeGatewayKey({
    onSuccess: () => {
      onOpenChange(false);
      onRevoked?.();
    },
  });

  const renameMutation = useRenameGatewayKey();

  if (!gatewayKey) return null;

  const isRevoked = gatewayKey.status === "revoked";
  const primaryProvider = getPrimaryProvider(gatewayKey.providers);
  const models = getModels(gatewayKey.providers);

  const titleText =
    gatewayKey.name + (primaryProvider ? ` — ${primaryProvider}` : "");

  const handleStartEditing = () => {
    setEditValue(gatewayKey.name);
    setIsEditing(true);
  };

  const handleConfirm = () => {
    const name = editValue.trim();
    if (!name || name === gatewayKey.name) {
      setIsEditing(false);
      return;
    }
    renameMutation.mutate(
      { id: gatewayKey.id, name },
      {
        onSuccess: (updated) => {
          setIsEditing(false);
          onRenamed?.(updated);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div
              className={cn(
                "flex size-11 shrink-0 items-center justify-center rounded-lg",
                isRevoked
                  ? "bg-slate-100 text-slate-400 ring-1 ring-slate-200 dark:bg-muted dark:text-muted-foreground dark:ring-border"
                  : "bg-[#0B1220] text-white dark:bg-foreground dark:text-background",
              )}
            >
              <Key className="size-5" />
            </div>
            <div className="min-w-0 flex-1">
              {isEditing ? (
                <div className="flex items-center gap-2 pr-8">
                  <Input
                    ref={inputRef}
                    autoFocus
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleConfirm();
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        setIsEditing(false);
                      }
                    }}
                    disabled={renameMutation.isPending}
                    className="h-9 text-sm font-semibold"
                  />
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={renameMutation.isPending}
                    className="flex size-8 shrink-0 items-center justify-center rounded-md bg-emerald-50 text-emerald-500 transition-colors hover:bg-emerald-100 disabled:opacity-50 dark:bg-emerald-950 dark:text-emerald-400 dark:hover:bg-emerald-900"
                  >
                    <Check className="size-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <DialogTitle className="text-lg font-semibold">
                    {titleText}
                  </DialogTitle>
                  {!isRevoked && canUpdate.data && (
                    <button
                      type="button"
                      onClick={handleStartEditing}
                      className="rounded-md p-1 text-muted-foreground/60 transition-colors hover:bg-muted hover:text-muted-foreground"
                    >
                      <Pencil className="size-3.5" />
                    </button>
                  )}
                </div>
              )}
              <DialogDescription className="mt-0.5 text-xs text-muted-foreground">
                {primaryProvider && `${primaryProvider} · `}
                {formatDate(gatewayKey.created_at)}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50/80 dark:border-border dark:bg-muted/80">
          <div className="grid grid-cols-[140px_1fr] text-sm">
            <div className="border-b border-slate-200 px-4 py-2.5 text-muted-foreground dark:border-border">
              Credential
            </div>
            <div className="border-b border-slate-200 px-4 py-2.5 text-right font-medium dark:border-border">
              {primaryProvider || "—"}
            </div>
            <div className="border-b border-slate-200 px-4 py-2.5 text-muted-foreground dark:border-border">
              Models
            </div>
            <div className="border-b border-slate-200 px-4 py-2.5 text-right font-medium dark:border-border">
              {models || "—"}
            </div>
            <div className="px-4 py-2.5 text-muted-foreground">Created</div>
            <div className="px-4 py-2.5 text-right font-medium">
              {formatDate(gatewayKey.created_at)}
            </div>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col space-y-2">
          <h4 className="shrink-0 text-xs font-semibold tracking-wider text-muted-foreground uppercase">
            Entity Access
          </h4>
          <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-border dark:bg-card">
            <div className="sticky top-0 z-10 grid grid-cols-[1fr_auto] border-b border-slate-200 bg-slate-50 px-4 py-2 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase dark:border-border dark:bg-muted">
              <span>Entity Type</span>
              <span>Access</span>
            </div>
            <div className="max-h-[280px] overflow-y-auto">
              {gatewayKey.entities.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                  No entities configured
                </div>
              ) : (
                [...gatewayKey.entities]
                  .sort((a, b) => {
                    const order: Record<AccessLabel, number> = {
                      Allowed: 0,
                      Blocked: 1,
                      Redact: 2,
                    };
                    return (
                      order[policyModeToAccess(a.policy_mode)] -
                      order[policyModeToAccess(b.policy_mode)]
                    );
                  })
                  .map((e, i) => {
                    const access = policyModeToAccess(e.policy_mode);
                    const color = accessColor[access];
                    return (
                      <div
                        key={e.id}
                        className={cn(
                          "grid grid-cols-[1fr_auto] items-center border-b border-slate-100 px-4 py-2.5 text-sm last:border-b-0 dark:border-border",
                          i % 2 === 1
                            ? "bg-slate-50/50 dark:bg-muted/50"
                            : "bg-white dark:bg-card",
                        )}
                      >
                        <span>{e.name}</span>
                        <span
                          className={cn(
                            "flex items-center gap-1.5 font-medium",
                            color.text,
                          )}
                        >
                          <span
                            className={cn("size-2 rounded-full", color.dot)}
                          />
                          {access}
                        </span>
                      </div>
                    );
                  })
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0 border-t border-slate-100 pt-4 sm:justify-between dark:border-border">
          {!isRevoked && canDelete.data ? (
            <Button
              variant="outline"
              className="gap-2 border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950 dark:hover:text-red-300"
              disabled={revokeMutation.isPending}
              onClick={() => revokeMutation.mutate(gatewayKey.id)}
            >
              <LockKeyholeOpen className="size-4" />
              {revokeMutation.isPending ? "Revoking…" : "Revoke Key"}
            </Button>
          ) : (
            <div />
          )}
          <DialogClose asChild>
            <Button variant="default">Close</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function usePaginatedGatewayKeys(status: "active" | "revoked") {
  const [page, setPage] = React.useState(1);
  const [items, setItems] = React.useState<Array<GatewayKeyItem>>([]);
  const [hasMore, setHasMore] = React.useState(false);
  const [count, setCount] = React.useState(0);

  const query = useGatewayKeysList(status, page, 10);

  React.useEffect(() => {
    if (!query.data) return;
    if (page === 1) {
      setItems(query.data.items);
    } else {
      setItems((prev) => [...prev, ...query.data.items]);
    }
    setHasMore(query.data.next_page !== null);
    setCount(query.data.count);
  }, [query.data, page]);

  const loadMore = () => setPage((p) => p + 1);

  const reset = () => {
    setPage(1);
    setItems([]);
    setHasMore(false);
  };

  const isLoading = page === 1 && query.isLoading;
  const isLoadingMore = page > 1 && query.isFetching;

  return { items, hasMore, loadMore, reset, isLoading, isLoadingMore, count };
}

function RouteComponent() {
  const [revokedOpen, setRevokedOpen] = React.useState(false);
  const [selectedKey, setSelectedKey] = React.useState<GatewayKeyItem | null>(
    null,
  );

  const active = usePaginatedGatewayKeys("active");
  const revoked = usePaginatedGatewayKeys("revoked");

  const canCreate = useResourceAccessPermission("gateway_keys", ["create"]);

  const handleRevoked = () => {
    active.reset();
    revoked.reset();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold">Gateway Keys</h1>
          <p className="max-w-[760px] text-sm text-muted-foreground">
            Bundle upstream provider credentials, model allows, and entity
            redaction policy into a single API token
          </p>
        </div>

        {canCreate.data && (
          <Button asChild>
            <Link to="/gateway-keys/create">
              <PlusIcon className="size-4" />
              Create Gateway Key
            </Link>
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-center dark:border-border dark:bg-card">
          <div className="mt-2 text-2xl font-semibold text-emerald-600 dark:text-emerald-400">
            {active.isLoading ? "—" : active.count}
          </div>
          <div className="text-xs font-medium text-muted-foreground">
            Active Keys
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-center dark:border-border dark:bg-card">
          <div className="mt-2 text-2xl font-semibold text-blue-600 dark:text-blue-400">
            {revoked.isLoading ? "—" : revoked.count}
          </div>
          <div className="text-xs font-medium text-muted-foreground">
            Revoked Keys
          </div>
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-xl border bg-muted/40 p-5">
        <div className="mt-2 flex size-9 items-center justify-center">
          <Zap className="size-4 text-blue-700 dark:text-blue-400" />
        </div>
        <div className="space-y-1 text-sm text-blue-700 dark:text-blue-400">
          <div className="font-medium">How Gateway Keys work:</div>
          <div>
            A gateway key bundles upstream provider credentials, model allows,
            and entity-level redaction policy into a single token. Callers swap
            their base URL to point at the Secure AI Gateway and use this token
            instead of raw provider API key. The gateway resolves the token at
            request time to enforce per-client policy — the raw upstream
            credentials are never exposed.
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {active.isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            Loading…
          </div>
        ) : active.items.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No active gateway keys
          </div>
        ) : (
          active.items.map((k) => (
            <KeyCard key={k.id} k={k} onClick={() => setSelectedKey(k)} />
          ))
        )}
        {active.isLoadingMore ? (
          <div className="py-4 text-center text-sm text-muted-foreground">
            Loading…
          </div>
        ) : (
          active.hasMore && (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={active.loadMore}
            >
              Show more
            </Button>
          )
        )}
      </div>

      <Collapsible open={revokedOpen} onOpenChange={(v) => setRevokedOpen(v)}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between py-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <p className="flex items-center gap-0.5">
              {" "}
              {revokedOpen ? (
                <ChevronDown className="size-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="size-4 text-muted-foreground" />
              )}
              Revoked Keys ({revoked.count})
            </p>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="mt-2 space-y-2">
            {revoked.isLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Loading…
              </div>
            ) : revoked.items.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No revoked keys
              </div>
            ) : (
              revoked.items.map((k) => (
                <KeyCard key={k.id} k={k} onClick={() => setSelectedKey(k)} />
              ))
            )}
            {revoked.isLoadingMore ? (
              <div className="py-4 text-center text-sm text-muted-foreground">
                Loading…
              </div>
            ) : (
              revoked.hasMore && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={revoked.loadMore}
                >
                  Show more
                </Button>
              )
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      <KeyDetailDialog
        gatewayKey={selectedKey}
        open={selectedKey !== null}
        onOpenChange={(v) => {
          if (!v) setSelectedKey(null);
        }}
        onRevoked={handleRevoked}
        onRenamed={(updated) =>
          setSelectedKey((prev) => (prev ? { ...prev, ...updated } : updated))
        }
      />
    </div>
  );
}
